import React, { useEffect, useState } from 'react';
import { Button, Tag, Space, Spin, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, CalendarOutlined, UserOutlined, EyeOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { getBlogPostById } from '../../../api/blogApi';
import moment from 'moment';

const BlogView = () => {
  const history = useHistory();
  const { id } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPost();
  }, [id]);

  const fetchBlogPost = async () => {
    setLoading(true);
    try {
      const data = await getBlogPostById(id);
      setBlogPost(data);
    } catch (error) {
      message.error('Failed to fetch blog post');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h3>Blog post not found</h3>
        <Button onClick={() => history.push('/dashboard/blog-posts')}>
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div className="blog-view-container">
      <div className="blog-view-header">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => history.push('/dashboard/blog-posts')}
        >
          Back to List
        </Button>
        <div className="blog-view-actions">
          <Button 
            type="primary"
            icon={<EditOutlined />}
            onClick={() => history.push(`/dashboard/blog-posts/edit/${id}`)}
          >
            Edit Post
          </Button>
        </div>
      </div>

      <h1>{blogPost.title}</h1>

      <div className="blog-meta">
        <div className="meta-item">
          <UserOutlined />
          <span className="meta-label">Author:</span>
          <span>{blogPost.author}</span>
        </div>
        <div className="meta-item">
          <CalendarOutlined />
          <span className="meta-label">Published:</span>
          <span>
            {blogPost.publishedDate 
              ? moment(blogPost.publishedDate).format('MMMM DD, YYYY') 
              : 'Not published'}
          </span>
        </div>
        <div className="meta-item">
          <EyeOutlined />
          <span className="meta-label">Views:</span>
          <span>{blogPost.viewCount || 0}</span>
        </div>
        <div className="meta-item">
          <Tag color={blogPost.published ? 'green' : 'orange'}>
            {blogPost.published ? 'Published' : 'Draft'}
          </Tag>
        </div>
      </div>

      {blogPost.excerpt && (
        <div style={{ 
          padding: '16px', 
          background: '#f5f5f5', 
          borderRadius: '4px',
          marginBottom: '24px',
          fontStyle: 'italic'
        }}>
          {blogPost.excerpt}
        </div>
      )}

      {blogPost.featuredImage && (
        <div className="blog-featured-image">
          <img src={blogPost.featuredImage} alt={blogPost.title} />
        </div>
      )}

      <div 
        className="blog-content"
        dangerouslySetInnerHTML={{ __html: blogPost.content }}
      />

      {blogPost.categories && blogPost.categories.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <strong>Categories: </strong>
          {blogPost.categories.map(cat => (
            <Tag key={cat} color="blue">{cat}</Tag>
          ))}
        </div>
      )}

      {blogPost.tags && blogPost.tags.length > 0 && (
        <div className="blog-tags">
          <strong>Tags: </strong>
          {blogPost.tags.map(tag => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </div>
      )}

      {(blogPost.metaTitle || blogPost.metaDescription || blogPost.metaKeywords) && (
        <div style={{ 
          marginTop: '32px', 
          padding: '16px', 
          background: '#fafafa',
          borderRadius: '4px'
        }}>
          <h3>SEO Information</h3>
          {blogPost.metaTitle && (
            <div style={{ marginBottom: '8px' }}>
              <strong>Meta Title:</strong> {blogPost.metaTitle}
            </div>
          )}
          {blogPost.metaDescription && (
            <div style={{ marginBottom: '8px' }}>
              <strong>Meta Description:</strong> {blogPost.metaDescription}
            </div>
          )}
          {blogPost.metaKeywords && (
            <div>
              <strong>Meta Keywords:</strong> {blogPost.metaKeywords}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogView;
